import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
    deleteHRM3110_R01, downloadExcel_HRM3110_R01, getHRM3110_R01AllCount,
    getHRM3110_R01List, patchHRM3110_R01, postHRM3110_R01,
} from "./Api_HRM3110_R01";
import { defaultHRM3110_R01Dto, HRM3110_R01Dto } from "./Dto_HRM3110_R01";
import { HRM3110_R01Filter, useHRM3110_R01Store } from "./Store_HRM3110_R01";
import {
    ALLOW_DEDUCT_TYPE_ALLOW, ALLOW_DEDUCT_TYPE_DEDUCT, getAllowDeductType, HRM3110_R01GridType,
    isSystemAllowDeductCode, TAX_TYPE_NONE, TAX_TYPE_NONTAX, TAX_TYPE_TAXABLE,
} from "./Util_HRM3110_R01";

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    allowGridRef: React.RefObject<AgGridReact<HRM3110_R01Dto> | null>; // 지급 그리드 ref. 편집 확정과 조회 후 스크롤 이동에 쓴다.
    deductGridRef: React.RefObject<AgGridReact<HRM3110_R01Dto> | null>; // 공제 그리드 ref. 편집 확정과 조회 후 스크롤 이동에 쓴다.
    setMessage: (message: string) => void; // 조회·저장 결과를 하단 메시지로 표시하는 함수
}
/** 조회 함수의 부가 옵션이다. messageMode가 none이면 조회 완료·빈 목록 알림을 표시하지 않는다. */
type FetchOptions = { messageMode?: 'show' | 'none' };

/**
 * 화면의 조회, 등록, 삭제 표시, 두 그리드 일괄 저장, Excel 동작을 구성한다.
 * @param allowGridRef 지급 그리드 참조
 * @param deductGridRef 공제 그리드 참조
 * @param setMessage 조회·저장 결과를 표시하는 화면 메시지 변경 함수
 * @returns Container가 버튼과 그리드에 연결할 상태 및 동작 함수
 */
export const HRM3110_R01Content = ({ allowGridRef, deductGridRef, setMessage }: Props) => {
    // 화면 Store에서 검색 조건, 두 그리드의 목록·선택·삭제 상태와 변경 함수를 가져온다.
    const {
        HRM3110_R01Filter, setHRM3110_R01PastFilter,
        HRM3110_R01AllowList, setHRM3110_R01AllowList, insertHRM3110_R01AllowList,
        HRM3110_R01DeductList, setHRM3110_R01DeductList, insertHRM3110_R01DeductList,
        setOriginalHRM3110_R01AllowList, setOriginalHRM3110_R01DeductList,
        setAllCount, setAllowScrollTop, setDeductScrollTop, initData,
    } = useHRM3110_R01Store();
    // 공통코드 Store에서 과세구분(TAX_TYPE) 목록을 가져온다. 저장 전 과세구분 값 검증에 쓴다.
    const { taxTypeList } = useCommonCodeStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다.
    const [clickable, setClickable] = useState(true);

    useFetchActionbarAuth("HRM3110_R01");

    /** 빈 검색값을 undefined로 바꿔 요청에서 제외할 조건 객체를 반환한다. */
    const getParams = useCallback((filter: HRM3110_R01Filter): Partial<HRM3110_R01Filter> => ({
        // ||: 빈 문자열이면 undefined로 바꿔 쿼리 파라미터에서 빠지게 한다.
        allowDeductType: filter.allowDeductType || undefined,
        taxType: filter.taxType || undefined,
        useYn: filter.useYn || undefined,
    }), []);

    /**
     * 목록과 전체 건수를 같은 조건으로 조회해 지급·공제 그리드에 나눠 넣고 선택·삭제 표시를 초기화한다.
     * 검색 조건은 초기화 직후에도 최신 값을 쓰도록 렌더 시점 값 대신 Store에서 직접 읽는다.
     * @param fetchType search는 스크롤을 초기화하고 refetch는 저장 뒤 목록을 다시 읽는다.
     * @param options messageMode가 none이면 조회 완료 또는 빈 목록 알림을 표시하지 않는다.
     * @returns 조회 완료를 나타내는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: FetchOptions) => {
        setLoadingSpinner(true);
        try {
            const filter = useHRM3110_R01Store.getState().HRM3110_R01Filter;
            // 사용자가 새 조회를 요청한 경우에만 이전 스크롤 위치를 버린다.
            if (fetchType === 'search') {
                setAllowScrollTop(null);
                setDeductScrollTop(null);
            }
            const response = await getHRM3110_R01List(fetchType, getParams(filter), 0);
            // 공통 인터셉터는 실패 응답도 오류 알림 후 그대로 넘겨주므로, 실패하면 기존 목록을 유지하고 끝낸다.
            if (!response.data.successFlag) return;
            const list = response.data.data;
            // 한 번 조회한 결과를 구분 A는 지급 그리드, D는 공제 그리드로 나눈다.
            const allowList = list.filter(row => row.allowDeductType === ALLOW_DEDUCT_TYPE_ALLOW);
            const deductList = list.filter(row => row.allowDeductType === ALLOW_DEDUCT_TYPE_DEDUCT);
            setHRM3110_R01AllowList(allowList);
            setHRM3110_R01DeductList(deductList);
            setOriginalHRM3110_R01AllowList(allowList);
            setOriginalHRM3110_R01DeductList(deductList);
            // 조회가 끝나면 두 그리드의 선택·삭제 표시를 모두 지운다.
            const state = useHRM3110_R01Store.getState();
            state.initSelectedHRM3110_R01AllowList();
            state.initSelectedHRM3110_R01DeductList();
            state.removeDeletedHRM3110_R01AllowList();
            state.removeDeletedHRM3110_R01DeductList();
            const countResponse = await getHRM3110_R01AllCount(getParams(filter));
            // 삼항 연산자: 건수 조회가 실패하면 data가 숫자가 아니므로 받은 목록 건수로 대신한다.
            const count = countResponse.data.successFlag ? countResponse.data.data : list.length;
            setAllCount(count);
            // 목록이 있으면 각 그리드를 첫 행으로 이동하고, 요청한 경우에만 완료 메시지를 표시한다.
            if (list.length > 0) {
                if (allowList.length > 0) allowGridRef.current?.api.ensureIndexVisible(0, 'top');
                if (deductList.length > 0) deductGridRef.current?.api.ensureIndexVisible(0, 'top');
                if (options?.messageMode !== 'none') {
                    ShowSuccessFetch(setMessage,
                        `조회가 완료되었습니다. (지급 ${allowList.length}건 · 공제 ${deductList.length}건, 총 ${list.length}/${count}건)`);
                }
            // 결과가 없고 메시지를 허용한 경우에는 빈 목록을 알린다.
            } else if (options?.messageMode !== 'none') {
                showWarning('데이터가 없습니다.');
            }
            setHRM3110_R01PastFilter(filter);
        } finally {
            // 조회 성공·실패와 관계없이 로딩 표시를 해제한다.
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, setAllowScrollTop, setDeductScrollTop, getParams,
        setHRM3110_R01AllowList, setHRM3110_R01DeductList, setOriginalHRM3110_R01AllowList,
        setOriginalHRM3110_R01DeductList, setAllCount, allowGridRef, deductGridRef, setMessage,
        setHRM3110_R01PastFilter]);

    /** 조회 버튼에서 일반 조회를 실행하고 조회 Promise를 반환한다. */
    const fetchData = useCallback(() => executeDataFetch('search'), [executeDataFetch]);

    /**
     * 그리드 종류에 맞는 신규 행 기본값을 반환한다.
     * 구분은 그리드로 정하고, 과세구분은 지급 T·공제 해당없음(-)으로 시작한다. 순서는 두 그리드 전체 최댓값 + 1이다.
     * @param gridType 행을 추가할 그리드
     * @returns 신규 행 기본값
     */
    const defaultHRM3110_R01Row = useCallback((gridType: HRM3110_R01GridType): HRM3110_R01Dto => ({
        ...defaultHRM3110_R01Dto,
        allowDeductType: getAllowDeductType(gridType),
        // 삼항 연산자: 지급 그리드는 과세(T), 공제 그리드는 해당없음(-)을 기본 과세구분으로 쓴다.
        taxType: gridType === 'allow' ? TAX_TYPE_TAXABLE : TAX_TYPE_NONE,
        inserted: true,
        // 같은 테이블의 순서이므로 지급·공제를 합친 최댓값 다음 번호를 준다.
        sortSeq: Math.max(0, ...[...HRM3110_R01AllowList, ...HRM3110_R01DeductList].map(row => row.sortSeq || 0)) + 1,
    }), [HRM3110_R01AllowList, HRM3110_R01DeductList]);

    /** 화면 Store와 안내 메시지를 초기화한다. 다른 화면과 같이 조회하지 않고 빈 화면으로 둔다. 콤보 선택지는 공통 Store에서 관리한다. */
    const initContainerData = useCallback(() => {
        initData();
        setMessage('');
    }, [initData, setMessage]);

    /**
     * 등록 버튼 또는 행의 + 버튼으로 신규 행을 만든다.
     * @param event 버튼 클릭 이벤트
     * @param rowIndex 값이 있으면 해당 그리드 행을 기준으로 삽입한다.
     * @param gridType 행을 추가할 그리드. 없으면(등록 버튼) 마지막으로 클릭한 그리드에 추가한다.
     * @returns 반환값 없이 Store 목록을 갱신한다.
     */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number, gridType?: HRM3110_R01GridType) => {
        // ??: + 버튼은 자기 그리드를, 등록 버튼은 마지막으로 클릭한 그리드를 대상으로 한다.
        // 대상 그리드는 렌더 시점 값이 아니라 Store에서 바로 읽는다. activeGridType을 의존성에 넣으면
        // 다른 그리드를 누르는 순간(mousedown) 이 함수가 새로 만들어지고, 이를 쓰는 그리드 컬럼 정의가 다시 만들어져
        // + 버튼이 mouseup 전에 다시 그려진다. 그러면 click 이벤트가 사라져 + 버튼이 첫 클릭에 동작하지 않는다. (HRM3050_R01과 동일)
        const targetGridType = gridType ?? useHRM3110_R01Store.getState().activeGridType;
        const isAllowGrid = targetGridType === 'allow';
        const targetList = isAllowGrid ? HRM3110_R01AllowList : HRM3110_R01DeductList;
        // 행 번호가 전달되면 행의 + 버튼 동작이므로 해당 행을 삽입 기준으로 사용한다.
        const isGridInsert = rowIndex !== undefined;
        const defaultRow = defaultHRM3110_R01Row(targetGridType);
        InsertRowHandler({
            event,
            dataList: targetList,
            rowData: isGridInsert ? targetList[rowIndex] : { ...defaultRow, valueIndex: -1 },
            insertRow: isAllowGrid ? insertHRM3110_R01AllowList : insertHRM3110_R01DeductList,
            defaultRowData: defaultRow,
            // 펼치기(...): + 버튼일 때만 setClickable 옵션을 넣어 같은 클릭이 행 선택으로 처리되지 않게 한다.
            ...(isGridInsert && { setClickable }),
        });
    }, [HRM3110_R01AllowList, HRM3110_R01DeductList, defaultHRM3110_R01Row,
        insertHRM3110_R01AllowList, insertHRM3110_R01DeductList]);

    /**
     * 저장 대상 행의 필수값, 과세구분, 순서, 비과세한도, 코드 중복을 순서대로 검증한다.
     * @param row 검증할 신규 또는 수정 행
     * @param activeRows 두 그리드에서 삭제 표시와 완전한 빈 신규 행을 제외한 비교 대상
     * @returns 모두 유효하면 true, 첫 실패 항목을 경고하고 저장을 막으면 false
     */
    const validateInsertData = useCallback((row: HRM3110_R01Dto, activeRows: HRM3110_R01Dto[]): boolean => {
        const code = row.allowDeductCd.trim().toUpperCase();
        // 공백 제거 뒤 코드가 없으면 필수값 오류를 알리고 검증을 중단한다.
        if (!code) { showWarning('코드를 입력해주세요.'); return false; }
        // 항목명이 빈 문자열 또는 공백뿐이면 저장할 수 없다.
        if (!row.allowDeductNm.trim()) { showWarning('항목명을 입력해주세요. > ' + code); return false; }
        // 과세구분이 없거나 TAX_TYPE 선택지에 없는 값이면 저장할 수 없다.
        if (!row.taxType || !taxTypeList.some(item => item.minorCd === row.taxType)) {
            showWarning('과세구분을 선택해주세요. > ' + code); return false;
        }
        // 순서는 0 이상의 정수여야 한다.
        if (!Number.isInteger(row.sortSeq) || row.sortSeq < 0) {
            showWarning('순서는 0 이상의 정수여야 합니다. > ' + code); return false;
        }
        // 비과세한도는 0 이상이어야 한다.
        if (row.nontaxLimit < 0) {
            showWarning('비과세한도는 0보다 작을 수 없습니다. > ' + code); return false;
        }
        // 비과세한도는 과세구분이 비과세인 지급 항목에만 넣을 수 있다. 메시지는 실행 샘플과 같다.
        if (row.allowDeductType === ALLOW_DEDUCT_TYPE_ALLOW && row.nontaxLimit > 0 && row.taxType !== TAX_TYPE_NONTAX) {
            showWarning(`비과세한도는 과세구분이 '비과세'인 항목에만 넣을 수 있습니다: ${row.allowDeductNm}`); return false;
        }
        // 지급·공제는 같은 테이블의 기본키를 쓰므로 두 그리드를 합쳐 코드 중복을 검사한다. 앞뒤 공백과 대소문자는 무시한다.
        if (activeRows.some(other => other !== row && other.allowDeductCd.trim().toUpperCase() === code)) {
            showWarning('중복된 코드가 있습니다. > ' + code); return false;
        }
        return true;
    }, [taxTypeList]);

    /**
     * 두 그리드의 편집값을 확정한 뒤 신규·수정·삭제 대상을 합쳐 일괄 요청한다.
     * 빈 신규 행은 제외하고, 검증 실패 또는 변경 없음이면 요청 없이 종료한다.
     * @returns 모든 요청과 성공 후 재조회가 완료되는 Promise<void>. 요청 오류는 전파한다.
     */
    const handleSaveClick = useCallback(async () => {
        allowGridRef.current?.api.stopEditing();
        deductGridRef.current?.api.stopEditing();
        // stopEditing이 발생시킨 최신 셀 값을 읽기 위해 렌더 시점의 목록 대신 Store를 다시 조회한다.
        const state = useHRM3110_R01Store.getState();

        /**
         * 한 그리드의 목록을 저장 대상별로 나눈다.
         * @param list 그리드 목록
         * @param deletedIds 그리드의 삭제 표시 식별값 목록
         * @returns 비교 대상(activeRows), 신규, 수정, 삭제 대상 목록
         */
        const splitGridRows = (list: HRM3110_R01Dto[], deletedIds: string[]) => {
            // 삭제 목록에는 숫자(행 클릭 후 삭제)와 문자열(전체 선택 후 삭제)이 섞일 수 있어 문자열로 맞춰 비교한다.
            const deletedSet = new Set(deletedIds.map(String));
            /** valueIndex 문자열이 삭제 표시 목록에 있으면 true를 반환한다. */
            const isDeleted = (row: HRM3110_R01Dto) => deletedSet.has(row.valueIndex.toString());
            /** 코드·항목명이 모두 비어 있는 신규 그리드 행이면 true를 반환한다. */
            const isBlank = (row: HRM3110_R01Dto) =>
                row.inserted && !row.allowDeductCd.trim() && !row.allowDeductNm.trim();
            const activeRows = list.filter(row => !isDeleted(row) && !isBlank(row));
            return {
                activeRows,
                insertedList: activeRows.filter(row => row.inserted),
                modifiedList: activeRows.filter(row => row.isModified && !row.inserted),
                deleteTargetList: list.filter(row => isDeleted(row) && !row.inserted),
            };
        };
        const allowRows = splitGridRows(state.HRM3110_R01AllowList, state.deletedHRM3110_R01AllowList);
        const deductRows = splitGridRows(state.HRM3110_R01DeductList, state.deletedHRM3110_R01DeductList);
        const activeRows = [...allowRows.activeRows, ...deductRows.activeRows];
        const insertedList = [...allowRows.insertedList, ...deductRows.insertedList];
        const modifiedList = [...allowRows.modifiedList, ...deductRows.modifiedList];
        const deleteTargetList = [...allowRows.deleteTargetList, ...deductRows.deleteTargetList];

        // 저장 대상이 하나도 없으면 서버 요청을 보내지 않는다.
        if (insertedList.length + modifiedList.length + deleteTargetList.length === 0) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        for (const row of [...insertedList, ...modifiedList]) {
            // 한 행이라도 검증에 실패하면 세 종류의 저장 요청을 모두 중단한다.
            if (!validateInsertData(row, activeRows)) return;
        }
        /** 서버에 보낼 코드를 대문자로 정리하고, 공제 행의 통상임금·비과세한도를 N·0으로 맞춘 행을 반환한다. */
        const normalize = (row: HRM3110_R01Dto): HRM3110_R01Dto => ({
            ...row,
            allowDeductCd: row.allowDeductCd.trim().toUpperCase(),
            // 삼항 연산자: 공제 행은 화면에 없는 통상임금·비과세한도를 고정값으로, 지급 행은 입력값 그대로 보낸다.
            ordinaryYn: row.allowDeductType === ALLOW_DEDUCT_TYPE_DEDUCT ? 'N' : row.ordinaryYn,
            nontaxLimit: row.allowDeductType === ALLOW_DEDUCT_TYPE_DEDUCT ? 0 : row.nontaxLimit,
        });
        // 신규·수정·삭제 대상이 있는 요청만 실행하고 모두 성공해야 완료 처리한다.
        const responseList = await Promise.all([
            insertedList.length > 0 ? postHRM3110_R01(insertedList.map(normalize)) : Promise.resolve(undefined),
            modifiedList.length > 0 ? patchHRM3110_R01(modifiedList.map(normalize)) : Promise.resolve(undefined),
            deleteTargetList.length > 0 ?
                deleteHRM3110_R01(deleteTargetList.map(row => row.allowDeductCd).join(',')) : Promise.resolve(undefined),
        ]);
        // 실행하지 않은 요청(undefined)은 빼고, 실제로 보낸 요청의 성공 여부만 본다.
        // 타입 가드: filter 결과에서 undefined가 빠졌다고 TypeScript에 알려 준다.
        const sentResponseList = responseList.filter(
            (response): response is NonNullable<typeof response> => response !== undefined);
        // 공통 인터셉터는 실패 응답도 서버 안내 문장으로 알린 뒤 그대로 넘겨주므로, 하나라도 실패하면 완료 알림을 띄우지 않는다.
        if (!sentResponseList.every(response => response.data.successFlag)) {
            // 세 요청은 각각 따로 저장되므로 일부만 성공했으면 DB 상태를 화면에 맞추기 위해 재조회한다.
            // 모두 실패했으면 사용자가 고쳐서 다시 저장할 수 있도록 편집 상태를 그대로 둔다.
            if (sentResponseList.some(response => response.data.successFlag)) {
                await executeDataFetch('refetch', { messageMode: 'none' });
            }
            return;
        }
        showSuccess('수당·공제코드 저장이 완료되었습니다.');
        setMessage('수당·공제코드 저장이 완료되었습니다.');
        await executeDataFetch('refetch', { messageMode: 'none' });
    }, [allowGridRef, deductGridRef, validateInsertData, setMessage, executeDataFetch]);

    /**
     * 삭제 버튼의 표시·복구 동작을 마지막으로 클릭한 그리드에 처리한다. 신규 행은 즉시 목록에서 제거하고 시스템 항목은 제외한다.
     * @returns 반환값 없이 선택 및 삭제 표시 상태를 갱신한다.
     */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM3110_R01Store.getState();
        const isAllowGrid = state.activeGridType === 'allow';
        // 삼항 연산자: 대상 그리드에 맞는 목록·선택·삭제 상태와 함수를 고른다.
        const gridList = isAllowGrid ? state.HRM3110_R01AllowList : state.HRM3110_R01DeductList;
        const selectedList = isAllowGrid ? state.selectedHRM3110_R01AllowList : state.selectedHRM3110_R01DeductList;
        const deletedList = isAllowGrid ? state.deletedHRM3110_R01AllowList : state.deletedHRM3110_R01DeductList;
        const removeRow = isAllowGrid ? state.removeHRM3110_R01AllowList : state.removeHRM3110_R01DeductList;
        const removeSelected = isAllowGrid ? state.removeSelectedHRM3110_R01AllowList : state.removeSelectedHRM3110_R01DeductList;
        const addDeleted = isAllowGrid ? state.addDeletedHRM3110_R01AllowList : state.addDeletedHRM3110_R01DeductList;
        const removeDeleted = isAllowGrid ? state.removeDeletedHRM3110_R01AllowList : state.removeDeletedHRM3110_R01DeductList;

        // 삭제 표시가 이미 있으면 버튼 동작을 대상 그리드 전체 복구로 처리한다.
        if (deletedList.length > 0) {
            removeDeleted();
            removeSelected();
            return;
        }
        // 선택된 행이 없으면 삭제할 대상이 없으므로 종료한다.
        if (selectedList.length === 0) return;
        // 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 섞일 수 있어 문자열로 맞춰 비교한다.
        const selectedSet = new Set(selectedList.map(String));
        const newRows = gridList.filter(row => selectedSet.has(String(row.valueIndex)) && row.inserted);
        newRows.forEach(row => removeRow(row.valueIndex));
        // 서버에 이미 있는 선택 행 중 시스템 항목은 삭제 표시에서 제외한다.
        const existingRows = gridList.filter(row => selectedSet.has(String(row.valueIndex)) && !row.inserted);
        const deletableRows = existingRows.filter(row => !isSystemAllowDeductCode(row.allowDeductCd));
        if (deletableRows.length < existingRows.length) {
            showWarning('시스템 항목은 삭제할 수 없습니다. 사용하지 않으려면 사용을 N으로 바꾸세요.');
        }
        // 선택 목록의 값을 형 변환 없이 그대로 삭제 목록으로 옮긴다.
        // 행 클릭 Hook과 행 번호 D 표시가 숫자 그대로 비교하므로, 같은 형을 유지해야 삭제 행 선택 차단과 D 표시가 맞는다. (HRM2130_R01과 동일)
        const existingIds = selectedList.filter(id =>
            deletableRows.some(row => String(row.valueIndex) === String(id)));
        // 서버에 이미 존재하는 행만 저장 시 삭제할 목록에 표시한다.
        if (existingIds.length > 0) addDeleted(existingIds);
        removeSelected();
    }, []);

    /**
     * 현재 검색 조건으로 지급·공제 전체 Excel 데이터를 요청하고 파일 다운로드를 시작한다.
     * @returns 다운로드 응답 처리가 끝나는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM3110_R01('search', getParams(HRM3110_R01Filter), 0);
            // 실패 응답은 파일 데이터가 없으므로 다운로드하지 않는다. 알림은 공통 인터셉터가 한 번 띄운다.
            if (!response.data.successFlag) return;
            downloadSettingExcel({ responseData: response.data.data, fileName: '수당·공제코드 등록-excel.xlsx' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, getParams, HRM3110_R01Filter]);

    // 화면을 열 때 자동 조회하지 않고, 조회 버튼을 눌러야 조회한다. 그리드 목록이 비어 있으면(최초 진입·초기화·0건 조회)
    // 바로 입력할 수 있도록 그 그리드에 빈 신규 행을 하나 넣는다. (PRD2010_R01 표준 패턴과 동일)
    // 의존성: 지급 목록이 바뀔 때마다 비었는지 다시 확인한다.
    useEffect(() => {
        if (HRM3110_R01AllowList.length === 0) {
            handleInsertClick({} as React.MouseEvent, undefined, 'allow');
            // 등록 처리 중 행 클릭이 막히지 않도록 클릭 가능 상태로 되돌린다.
            setClickable(true);
        }
    }, [HRM3110_R01AllowList, handleInsertClick]);
    // 의존성: 공제 목록이 바뀔 때마다 비었는지 다시 확인한다.
    useEffect(() => {
        if (HRM3110_R01DeductList.length === 0) {
            handleInsertClick({} as React.MouseEvent, undefined, 'deduct');
            setClickable(true);
        }
    }, [HRM3110_R01DeductList, handleInsertClick]);

    // Container가 필요한 함수만 노출한다. 데이터 값은 Store에서 읽는다.
    return {
        clickable, setClickable, executeDataFetch,
        fetchData, defaultHRM3110_R01Row, initContainerData, handleInsertClick,
        handleSaveClick, handleDeleteClick, handleExcelClick,
    };
};
